import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Select, InputNumber, Spin, Empty, Input } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiEncargosInstance, authHeaderEncargos } from "../../redux/actions/encargos/encargos";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const ACTION_CONFIG = {
  CREATE: { label: "Creacion", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

const ESTADO_SOLICITADO = 1;
const ESTADO_AVISADO = 2;
const ESTADO_ENTREGADO = 3;
const ESTADO_CANCELADO = 4;
const ESTADO_PEDIDO_PROVEEDOR = 5;
const ESTADO_DISPONIBLE = 6;

const ESTADOS = {
  [ESTADO_SOLICITADO]: "Solicitado",
  [ESTADO_AVISADO]: "Cliente avisado",
  [ESTADO_ENTREGADO]: "Entregado",
  [ESTADO_CANCELADO]: "Cancelado",
  [ESTADO_PEDIDO_PROVEEDOR]: "Pedido a proveedor",
  [ESTADO_DISPONIBLE]: "Disponible en tienda",
};

export default function EncargoDetallePage() {
  const { idEncargo } = useParams();
  const navigate = useNavigate();

  const [encargo, setEncargo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("datos");
  const [avanzando, setAvanzando] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [motivo, setMotivo] = useState("");

  const [entregarOpen, setEntregarOpen] = useState(false);
  const [formaPago, setFormaPago] = useState(1);
  const [formasPago, setFormasPago] = useState([]);
  const [montoTotal, setMontoTotal] = useState(null);

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchEncargo = useCallback(() => {
    setLoading(true);
    apiEncargosInstance.get(`/encargos/${idEncargo}`, { headers: authHeaderEncargos() })
      .then((res) => setEncargo(res.data))
      .catch(() => toast.error("No se pudo cargar el registro"))
      .finally(() => setLoading(false));
  }, [idEncargo]);

  useEffect(() => { fetchEncargo(); }, [fetchEncargo]);

  useEffect(() => {
    apiEncargosInstance.get("/formas-pago", { headers: authHeaderEncargos() })
      .then((r) => setFormasPago((r.data?.items ?? []).map((f) => ({ label: f.nombre, value: f.id_forma_pago }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    apiEncargosInstance.get(`/encargos/${idEncargo}/actividad`, { headers: authHeaderEncargos() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idEncargo]);

  const avanzarEstado = async (nuevo_estado) => {
    setAvanzando(true);
    try {
      await apiEncargosInstance.post(`/encargos/${idEncargo}/estado`, { nuevo_estado }, { headers: authHeaderEncargos() });
      toast.success("Estado actualizado");
      fetchEncargo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cambiar el estado");
    } finally {
      setAvanzando(false);
    }
  };

  const handleCancelar = async () => {
    if (!motivo.trim()) return toast.warning("Escribe un motivo de cancelacion");
    setAvanzando(true);
    try {
      await apiEncargosInstance.post(`/encargos/${idEncargo}/estado`, { nuevo_estado: ESTADO_CANCELADO, motivo_cancelacion: motivo }, { headers: authHeaderEncargos() });
      toast.success("Registro cancelado");
      setCancelOpen(false);
      setMotivo("");
      fetchEncargo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cancelar");
    } finally {
      setAvanzando(false);
    }
  };

  const handleEntregar = async () => {
    if (!encargo.id_variante && montoTotal == null) {
      toast.warning("Captura el monto total a cobrar");
      return;
    }
    setAvanzando(true);
    try {
      await apiEncargosInstance.post(`/encargos/${idEncargo}/estado`, {
        nuevo_estado: ESTADO_ENTREGADO,
        id_forma_pago: formaPago,
        monto_total: encargo.id_variante ? undefined : montoTotal,
      }, { headers: authHeaderEncargos() });
      toast.success("Entregado y cobrado");
      setEntregarOpen(false);
      fetchEncargo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo entregar");
    } finally {
      setAvanzando(false);
    }
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!encargo) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Registro no encontrado" />
      </div>
    );
  }

  const estado = encargo.id_estado_encargo;
  const esApartado = encargo.tipo === "apartado";
  const totalEstimado = encargo.id_variante ? parseFloat(encargo.precio_venta || 0) : null;
  const restante = totalEstimado != null ? Math.max(totalEstimado - parseFloat(encargo.anticipo || 0), 0) : Math.max((montoTotal || 0) - parseFloat(encargo.anticipo || 0), 0);

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{esApartado ? "Apartado" : "Encargo"} — {encargo.descripcion}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-calendar" /> Registrado el {fmtFechaCorta(encargo.datetime)}</span>
                <span className="cd-meta-item"><i className="fa-solid fa-user" /> {encargo.cliente_nombre}</span>
              </div>
            </div>
            <div className="cd-header-actions" style={{ flexWrap: "wrap", gap: 8 }}>
              {!esApartado && estado === ESTADO_SOLICITADO && (
                <Button type="primary" loading={avanzando} onClick={() => avanzarEstado(ESTADO_PEDIDO_PROVEEDOR)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                  Marcar pedido a proveedor
                </Button>
              )}
              {!esApartado && estado === ESTADO_PEDIDO_PROVEEDOR && (
                <Button type="primary" loading={avanzando} onClick={() => avanzarEstado(ESTADO_DISPONIBLE)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                  Marcar disponible en tienda
                </Button>
              )}
              {esApartado && estado === ESTADO_SOLICITADO && (
                <>
                  <Button loading={avanzando} onClick={() => avanzarEstado(ESTADO_DISPONIBLE)}>Marcar disponible</Button>
                  <Button type="primary" loading={avanzando} onClick={() => avanzarEstado(ESTADO_AVISADO)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                    Avisar cliente
                  </Button>
                </>
              )}
              {estado === ESTADO_DISPONIBLE && (
                <Button type="primary" loading={avanzando} onClick={() => avanzarEstado(ESTADO_AVISADO)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                  Avisar cliente
                </Button>
              )}
              {estado === ESTADO_AVISADO && (
                <Button type="primary" loading={avanzando} onClick={() => setEntregarOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                  Entregar y cobrar
                </Button>
              )}
              {estado < ESTADO_ENTREGADO && (
                <Button danger onClick={() => setCancelOpen(true)}>Cancelar</Button>
              )}
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {estado === ESTADO_CANCELADO && (
          <div className="cd-card" style={{ background: "#fee2e2", border: "1px solid #fecaca", marginBottom: 16 }}>
            <strong style={{ color: "#b91c1c" }}>Este registro fue cancelado.</strong>
            <div style={{ marginTop: 6, color: "#7f1d1d" }}>{encargo.motivo_cancelacion}</div>
            <div style={{ marginTop: 4, fontSize: 12, color: "#991b1b" }}>Por {encargo.cancelado_por || "—"} el {fmtDatetime(encargo.fecha_cancelacion)}</div>
          </div>
        )}

        {activeTab === "datos" && (
          <div className="cd-bento-grid" style={{ gridTemplateColumns: "2fr 1fr" }}>
            <div className="cd-card cd-card-client">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-box-archive"></i></div>
                <h2 className="cd-card-title">Datos del registro</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                <div><span className="cd-field-label">Tipo</span><span className="cd-field-value" style={{ textTransform: "capitalize" }}>{encargo.tipo}</span></div>
                <div><span className="cd-field-label">Cliente</span><span className="cd-field-value">{encargo.cliente_nombre}</span></div>
                {encargo.proveedor_nombre && <div><span className="cd-field-label">Proveedor</span><span className="cd-field-value">{encargo.proveedor_nombre}</span></div>}
                {encargo.producto_nombre && <div><span className="cd-field-label">Producto vinculado</span><span className="cd-field-value">{encargo.producto_nombre} ({encargo.clave})</span></div>}
                <div style={{ gridColumn: "1 / -1" }}><span className="cd-field-label">Descripcion</span><span className="cd-field-value" style={{ fontWeight: 400 }}>{encargo.descripcion}</span></div>
              </div>
            </div>

            <div className="cd-card">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-coins"></i></div>
                <h2 className="cd-card-title">Estado y costos</h2>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><span className="cd-field-label">Estado actual</span><span className="cd-field-value">{ESTADOS[estado]}</span></div>
                {encargo.precio_venta != null && <div><span className="cd-field-label">Precio de venta</span><span className="cd-field-value">{money(encargo.precio_venta)}</span></div>}
                <div><span className="cd-field-label">Anticipo</span><span className="cd-field-value">{money(encargo.anticipo)}</span></div>
                {encargo.id_venta_entrega && (
                  <div>
                    <span className="cd-field-label">Venta de entrega</span>
                    <span className="cd-field-value">
                      <a onClick={() => navigate(`/app/ventas/${encargo.id_venta_entrega}`)} style={{ color: "#f90336", cursor: "pointer" }}>Ver venta #{encargo.id_venta_entrega}</a>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal open={cancelOpen} onCancel={() => setCancelOpen(false)} onOk={handleCancelar} okText="Cancelar registro" okButtonProps={{ danger: true }} cancelText="Volver" title="Cancelar registro" confirmLoading={avanzando} centered>
        <Input.TextArea rows={3} placeholder="Motivo de cancelacion" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </Modal>

      <Modal open={entregarOpen} onCancel={() => setEntregarOpen(false)} onOk={handleEntregar} okText="Confirmar entrega y cobro" cancelText="Cancelar" title="Entregar y cobrar" confirmLoading={avanzando} centered okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {encargo.id_variante ? (
            <div>Precio de venta: <strong>{money(encargo.precio_venta)}</strong></div>
          ) : (
            <InputNumber style={{ width: "100%" }} placeholder="Monto total a cobrar" min={0} precision={2} value={montoTotal} onChange={setMontoTotal} />
          )}
          <div>Anticipo ya recibido: <strong>{money(encargo.anticipo)}</strong></div>
          <div>Restante a cobrar: <strong>{money(restante)}</strong></div>
          {restante > 0 && (
            <Select options={formasPago} value={formaPago} onChange={setFormaPago} placeholder="Forma de pago" />
          )}
        </div>
      </Modal>
    </div>
  );
}
